<template>
  <div class="client">
    <h2 class="page-title">Client Profile</h2>

    <div class="button-container">
      <div class="button-row">
        <button class="back-button" @click="editing ? editing = !editing : goBack()">Back</button>
        <button v-if="!editing" class="edit-button" @click="startEditing">Edit</button>
      </div>
      <div v-if="!editing" class="button-row">
        <div class="sync-controls">
          <span class="sync-label">Sync From Zaui VIA:</span>
          <div class="sync-toggle">
            <button
              class="sync-toggle-btn sync-toggle-left"
              :disabled="syncing || !hasZauiId"
              :title="hasZauiId ? '' : 'This client has no Zaui ID on file - use Last Name instead'"
              @click="syncClientWithZaui(false)"
            >
              Last Reservation
            </button>
            <button
              class="sync-toggle-btn sync-toggle-right"
              :disabled="syncing"
              @click="syncClientWithZaui(true)"
            >
              Last Name
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Panel for Zaui Sync -->
    <section v-if="showConfirmation" class="confirmation-panel">
      <h3>⚠️ Confirm Zaui Data Match</h3>
      <p class="confirmation-message">
        Found a potential match in Zaui{{ confirmationMatchMethodLabel }}. Please verify this is the correct person before syncing.
      </p>

      <div class="data-comparison">
        <div class="data-column zaui-data">
          <h4>Zaui Data</h4>
          <p><strong>Name:</strong> {{ confirmationData.zauiData.firstName }} {{ confirmationData.zauiData.lastName }}</p>
          <p><strong>Email:</strong> {{ confirmationData.zauiData.email }}</p>
          <p><strong>Mobile:</strong> {{ confirmationData.zauiData.mobilePhone }}</p>
          <p><strong>Country:</strong> {{ confirmationData.zauiData.country }}</p>
          <p><strong>Birth Date:</strong> {{ confirmationData.zauiData.birthDate }}</p>
          <p><strong>Booking #:</strong> {{ confirmationData.zauiData.bookingNumber }}</p>

          <div v-if="confirmationData.zauiData.customFields && confirmationData.zauiData.customFields.length > 0" class="custom-fields-preview">
            <h5>Custom Fields ({{ confirmationData.zauiData.customFields.length }} total)</h5>
            <div class="custom-fields-scroll">
              <p v-for="(field, index) in confirmationData.zauiData.customFields" :key="index" class="custom-field-item">
                <strong>{{ field.label }}:</strong> {{ field.value }}
              </p>
            </div>
          </div>
        </div>

        <div class="data-column current-data">
          <h4>Current Data</h4>
          <p><strong>Name:</strong> {{ confirmationData.currentData.firstName }} {{ confirmationData.currentData.lastName }}</p>
          <p><strong>Email:</strong> {{ confirmationData.currentData.email }}</p>
          <p><strong>Mobile:</strong> {{ confirmationData.currentData.mobilePhone }}</p>
        </div>
      </div>

      <div class="confirmation-buttons">
        <button class="confirm-button" @click="confirmSync" :disabled="syncing">
          {{ syncing ? 'Syncing...' : 'Confirm & Sync' }}
        </button>
        <button class="cancel-button" @click="cancelSync" :disabled="syncing">
          Cancel
        </button>
      </div>
    </section>

    <section v-if="!editing">
      <section class="profile-section">
        <h3>Person Details</h3>
        <div class="profile-container">
          <div class="profile-photo">
            <div class="photo-placeholder">Future Client Photo Spot</div>
          </div>
          <div class="profile-details" v-if="person">
            <p><strong>First Name:</strong> {{ person.firstname }}</p>
            <p><strong>Last Name:</strong> {{ person.lastname }}</p>
            <p><strong>Mobile Phone:</strong> {{ person.mobilephone }}</p>
            <p><strong>Email:</strong> {{ person.email }}</p>
            <p><strong>Country:</strong> {{ person.country }}</p>
            <p><strong>Date of Birth:</strong> {{ person.dateofbirth }}</p>
            <p><strong>Weight:</strong> {{ person.weight }} lbs</p>
            <p v-if="person.zauiclientid"><strong>Zaui ID:</strong> {{ person.zauiclientid }}</p>
          </div>
          <div class="loading" v-else>
            <p>Loading person details...</p>
          </div>
        </div>
      </section>

      <section class="profile-section">
        <h3>Health Records</h3>
        <div v-if="person">
          <div v-for="healthRecord in healthRecords" :key="healthRecord.id" class="health-record">
            <p><strong>Health Record ID:</strong> {{ healthRecord.id }}</p>
            <p><strong>Description:</strong> {{ healthRecord.description }}</p>
            <p><strong>Severity:</strong> {{ healthRecord.severity.description }}</p>
          </div>
        </div>
        <div v-else>
          <p>Loading person details...</p>
        </div>
      </section>

      <section class="profile-section">
        <h3 @click="toggleCustomFields" class="collapsible-header">
          Zaui Fields <span>{{ isCustomFieldsCollapsed ? '▼' : '▲' }}</span>
        </h3>
        <div v-show="!isCustomFieldsCollapsed">
          <div v-if="person && filteredCustomFields.length > 0">
            <div v-for="customField in filteredCustomFields" :key="customField.field_name" class="custom-field">
              <p><strong>{{ customField.field_name }}:</strong> {{ customField.field_value }}</p>
            </div>
          </div>
          <div v-else>
            <p>No custom fields available.</p>
          </div>
        </div>
      </section>

      <section>
        <h3>Trip History</h3>
        <!-- Future implementation for trip history -->
      </section>

      <section>
        <h3>Training Details</h3>
        <!-- Display and edit training details -->
        <div v-if="trainingDetails.length">
        <ul>
          <li v-for="(training, index) in trainingDetails" :key="index">
            Training Date: {{ training }}
          </li>
        </ul>
        </div>
      <div v-else>
        <p>No training details available.</p>
      </div>
      </section>
    </section>

    <!-- EDITING SECTION -->
    <section v-if="editing" class="edit-section">
      <h3>Edit Client Details</h3>
      <form @submit.prevent="updateClient">
        <div class="form-group">
          <label for="firstName">First Name</label>
          <input type="text" class="form-control" id="firstName" :placeholder="person.firstname" v-model="editableClient.firstname">
        </div>
        <div class="form-group">
          <label for="lastName">Last Name</label>
          <input type="text" class="form-control" id="lastName" :placeholder="person.lastname" v-model="editableClient.lastname">
        </div>
        <div class="form-group">
          <label for="mobilePhone">Mobile Phone</label>
          <input type="text" class="form-control" id="mobilePhone" :placeholder="person.mobilephone" v-model="editableClient.mobilephone">
        </div>
        <div class="form-group">
          <label for="email">Email</label>
          <input type="email" class="form-control" id="email" :placeholder="person.email" v-model="editableClient.email">
        </div>
        <div class="form-group">
          <label for="country">Country</label>
          <input type="text" class="form-control" id="country" :placeholder="person.country" v-model="editableClient.country">
        </div>
        <div class="form-group">
          <label>Date of Birth</label>
          <DatePicker v-model="editableClient.dateofbirth" clearable />
        </div>
        <div class="form-group">
          <label for="weight">Weight</label>
          <input type="number" class="form-control" id="weight" :placeholder="person.weight" v-model="editableClient.weight">
        </div>

        <!-- Health Record Details Form Fields -->
        <div v-if="editableHealthRecords.length">
          <h4>Health Record</h4>
          <div v-for="(record, index) in editableHealthRecords" :key="index">
            <div class="form-group">
              <label for="description">Description</label>
              <input type="text" class="form-control" id="description" v-model="record.description">
            </div>
            <div class="form-group">
              <label for="severity">Severity</label>
              <select class="form-control" id="severity" v-model="record.severityid">
                <option v-for="severity in severities" :value="severity.id" :key="severity.id">
                  {{ severity.description }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <button type="submit" class="btn btn-primary">Submit</button>
      </form>
    </section>
  </div>
</template>

<script>
import ClientsDataService from "@/services/ClientsDataService";
import HealthDataService from "@/services/HealthDataService";
import ZauiDataService from "@/services/ZauiDataService";
import DatePicker from "../Shared/DatePicker.vue";

export default {
  name: 'Client',
  components: { DatePicker },
  props: ['id'],
  data() {
    return {
      editing: false,
      client: null,
      person: null,
      healthRecords: [],
      trainingDetails: [],
      editableClient: null,
      editableHealthRecords: [],
      severities: [],
      isCustomFieldsCollapsed: true, // State to manage the collapse of custom fields
      syncing: false, // State to manage sync button loading
      showConfirmation: false, // State to show/hide confirmation panel
      confirmationData: null, // Data for confirmation panel
    };
  },
  methods: {
    startEditing() {
      this.editing = true;
      this.editableClient = JSON.parse(JSON.stringify(this.client));
      this.editableHealthRecords = JSON.parse(JSON.stringify(this.client.person.healthRecords));
      this.fetchSeverities();
    },
    goBack() {
      this.$router.go(-1);
    },
    fetchSeverities() {
      HealthDataService.fetchSeverities()
        .then(response => {
          this.severities = response.data;
        })
        .catch(e => {
          console.log(e);
        });
    },
    fetchClientData() {
      // Fetch client data by id
      ClientsDataService.get(this.id)
        .then(response => {
          console.log(response);
          this.client = response.data;
          this.person = response.data.person;
          this.customFields = response.data.person.customFields;
          // Additional fetch calls to get health records and training details
          this.healthRecords = response.data.person.healthRecords;
          this.trainingDetails = response.data.trainingDetails || [];
        })
        .catch(e => {
          console.error(e);
        });
    },
    updateClient() {
      // Prepare data to be sent, including health records
      const updateData = {
        client: this.editableClient,
        healthRecords: this.editableHealthRecords
      };
      // Make API call to update client and health records
      ClientsDataService.update(this.editableClient.clientid, updateData)
        .then(response => {
          // Update data with response
          this.client = response.data.client;
          this.person = response.data.person;
          this.healthRecords = response.data.person.healthRecords;
          this.editing = false;
        })
        .catch(e => {
          console.log(e);
        });
    },
    toggleCustomFields() {
      this.isCustomFieldsCollapsed = !this.isCustomFieldsCollapsed;
    },
    syncClientWithZaui(useLastNameSearch) {
      // Validate that we have the necessary client data
      if (!this.person || !this.person.email || !this.person.firstname || !this.person.lastname) {
        alert('Cannot sync: Client must have email, first name, and last name');
        return;
      }

      // Validate last name length if last name search is enabled
      if (useLastNameSearch && this.person.lastname.length <= 3) {
        if (!confirm('Last name search is not recommended for names shorter than 4 characters. Do you want to continue?')) {
          return;
        }
      }

      // Confirm with user before syncing
      if (!confirm(`This will update ${this.person.firstname} ${this.person.lastname}'s information with the latest data from Zaui. Continue?`)) {
        return;
      }

      this.syncing = true;

      // Prepare client data for Zaui sync
      const clientData = {
        personId: this.person.personid,
        email: this.person.email,
        firstName: this.person.firstname,
        lastName: this.person.lastname,
        mobilePhone: this.person.mobilephone || '',
        enableLastNameSearch: useLastNameSearch // Pass the flag to backend
      };

      // Call the Zaui sync API - a match always comes back needing confirmation, so the
      // user compares the found data against what's on file before anything is written.
      ZauiDataService.syncClientWithZaui(clientData)
        .then(response => {
          console.log('Sync response:', response.data);

          if (response.data.needsConfirmation) {
            this.handleSyncConfirmation(response.data);
          } else {
            alert('Sync completed but may not have updated all fields. Check console for details.');
          }
        })
        .catch(error => {
          console.error('Error syncing with Zaui:', error);

          let errorMessage = 'Failed to sync client data with Zaui';

          if (error.response && error.response.data && error.response.data.error) {
            errorMessage += ': ' + error.response.data.error;
          } else if (error.message) {
            errorMessage += ': ' + error.message;
          }

          alert(errorMessage);
        })
        .finally(() => {
          this.syncing = false;
        });
    },
    handleSyncConfirmation(data) {
      // Store confirmation data and show the confirmation panel
      this.confirmationData = data;
      this.showConfirmation = true;
      this.syncing = false; // Re-enable buttons
    },
    confirmSync() {
      // User confirmed - proceed with force sync
      if (!this.confirmationData || !this.confirmationData.zauiData) {
        alert('Error: No confirmation data available');
        return;
      }

      if (!this.person || !this.person.personid) {
        alert('Error: Current person data not available');
        return;
      }

      this.syncing = true;

      const syncData = {
        personId: this.person.personid, // Use the current person's ID
        zauiData: this.confirmationData.zauiData // Already-reviewed data from the confirmation panel
      };

      ZauiDataService.forceSyncClientWithZaui(syncData)
        .then(response => {
          console.log('Force sync response:', response.data);

          if (response.data.success) {
            // Hide confirmation panel
            this.showConfirmation = false;
            this.confirmationData = null;

            // Refresh the client data to show updated information
            this.fetchClientData();

            // Show success message after data is refreshed
            alert('Client data successfully synced with Zaui! The page has been refreshed with the latest data.');
          } else {
            alert('Sync completed but may not have updated all fields. Check console for details.');
          }
        })
        .catch(error => {
          console.error('Error force-syncing with Zaui:', error);

          let errorMessage = 'Failed to sync client data with Zaui';

          if (error.response && error.response.data && error.response.data.error) {
            errorMessage += ': ' + error.response.data.error;
          } else if (error.message) {
            errorMessage += ': ' + error.message;
          }

          alert(errorMessage);
        })
        .finally(() => {
          this.syncing = false;
        });
    },
    cancelSync() {
      // User cancelled - hide confirmation panel
      this.showConfirmation = false;
      this.confirmationData = null;
      this.syncing = false;
    }
  },
  created() {
    // Get the client by the ID
    this.fetchClientData();
  },
  computed: {
    hasZauiId() {
      return !!(this.person && this.person.zauiclientid);
    },
    confirmationMatchMethodLabel() {
      const method = this.confirmationData && this.confirmationData.lookupMethod;
      switch (method) {
        case 'zaui_id':
          return ' using their last reservation';
        case 'last_name_search':
          return ' using last name search';
        case 'booking_number':
        case 'no_booking_number':
          return ' using their booking details';
        default:
          return '';
      }
    },
    filteredCustomFields() {
      return this.customFields.filter(field => {
        // Check if field_value is not empty, not an empty object, and not an empty array
        const value = field.field_value;
        return value && value !== '{}' && (!Array.isArray(value) || value.length > 0);
      });
    }
  }
};
</script>

<style scoped>
.client {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
}

.profile-section {
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 20px;
  margin-bottom: 20px;
}

h3 {
  font-size: 18px;
}

.profile-container {
  display: flex;
  flex-direction: row;
}

.profile-photo {
  flex: 0 0 150px;
  height: 150px;
  margin-right: 20px;
}

.photo-placeholder {
  width: 100%;
  height: 100%;
  border: 2px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #999;
  text-align: center;
}

.profile-details {
  flex: 1;
}

.profile-details p {
  margin: 5px 0;
}

.health-record {
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 10px;
  margin-top: 10px;
}

.save-button {
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
}

.save-button:hover {
  background-color: #0056b3;
}

.button-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.button-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.button-row:last-child {
  justify-content: flex-end;
}

.sync-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sync-label {
  font-size: 14px;
  color: #555;
  white-space: nowrap;
}

.sync-toggle {
  display: inline-flex;
  border-radius: 999px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.sync-toggle-btn {
  background-color: #28a745;
  color: #fff;
  border: none;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
  white-space: nowrap;
}

.sync-toggle-left {
  border-right: 1px solid rgba(255, 255, 255, 0.4);
}

.sync-toggle-btn:hover:not(:disabled) {
  background-color: #218838;
}

.sync-toggle-btn:disabled {
  background-color: #a9d9b5;
  color: rgba(255, 255, 255, 0.8);
  cursor: not-allowed;
}

.edit-button {
  background-color: var(--primary-eph-color);
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
}

.back-button {
  background-color: #ccc;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
}

.edit-button:hover,
.back-button:hover {
  background-color: #999;
}

.back-button {
  background-color: #007bff;
}

.back-button:hover {
  background-color: #0056b3;
}

.collapsible-header {
  cursor: pointer;
}

/* Confirmation Panel Styles */
.confirmation-panel {
  background-color: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.confirmation-panel h3 {
  color: #856404;
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 20px;
}

.confirmation-message {
  color: #856404;
  margin-bottom: 20px;
  font-size: 14px;
}

.data-comparison {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  overflow-x: auto;
}

.data-column {
  flex: 1;
  min-width: 0;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 15px;
  overflow-wrap: break-word;
  word-break: break-word;
}

.data-column h4 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 16px;
  border-bottom: 2px solid #007bff;
  padding-bottom: 5px;
}

.zaui-data h4 {
  border-bottom-color: #28a745;
}

.data-column p {
  margin: 8px 0;
  font-size: 14px;
}

.custom-fields-preview {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.custom-fields-preview h5 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 14px;
  color: #666;
}

.custom-fields-scroll {
  max-height: 200px;
  overflow-y: auto;
  padding-right: 5px;
}

.custom-field-item {
  margin: 5px 0;
  font-size: 12px;
  padding: 5px;
  background-color: #f8f9fa;
  border-radius: 3px;
}

.confirmation-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.confirm-button,
.cancel-button {
  padding: 12px 30px;
  font-size: 16px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.confirm-button {
  background-color: #28a745;
  color: white;
}

.confirm-button:hover:not(:disabled) {
  background-color: #218838;
}

.cancel-button {
  background-color: #dc3545;
  color: white;
}

.cancel-button:hover:not(:disabled) {
  background-color: #c82333;
}

.confirm-button:disabled,
.cancel-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
